import { useState } from "react";
import { isStorageAvailable } from "@shared/utils/storage/cache-storage";
import { IPublicExchangeManager } from "@shared/modules/exchanges/interfaces/IPublicExchangeManager";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import useUserBasedDiscountService from "@app-contexts/public/features/discounts/data/services/useUserBasedDiscountService";
import { getFormattedPrice } from "@shared/modules/financials/utils/price-utils";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";

/**
 * Reads exchange credit data from localStorage synchronously.
 * Returns parsed data if valid and not expired, otherwise null.
 * Cleans up expired entries as a side effect.
 */
function readExchangeCreditData(): ExchangeCreditLocalStorageData | null {
  const raw = isStorageAvailable("localStorage")
    ? localStorage.getItem("exchangeCreditData")
    : null;

  if (!raw) return null;

  const data = JSON.parse(raw) as ExchangeCreditLocalStorageData;

  if (new Date().getTime() > data.expiry_time) {
    localStorage.removeItem("exchangeCreditData");
    return null;
  }

  return data;
}

interface ExchangeCreditLocalStorageData {
  expiry_time: number;
  credit_amount: number;
  currency: Parameters<typeof getFormattedPrice>[0]["currency"];
  venue: { id: number; slug: string; name: string } | null;
  is_membership_ticket_exchange?: boolean;
  is_member_item_exchange?: boolean;
  is_itemized?: boolean;
  total_tickets?: number | null;
}

interface ExchangeCreditState {
  isExchangeActive: boolean;
  formattedAmount: string | null;
  venueData: { id: number; slug: string; name: string } | null;
  isMembershipTicketExchange: boolean;
  isItemizedExchange: boolean;
  totalTickets: number | null;
}

function buildExchangeState(
  data: ExchangeCreditLocalStorageData | null
): ExchangeCreditState {
  if (!data) {
    return {
      isExchangeActive: false,
      formattedAmount: null,
      venueData: null,
      isMembershipTicketExchange: false,
      isItemizedExchange: false,
      totalTickets: null,
    };
  }

  return {
    isExchangeActive: true,
    formattedAmount: getFormattedPrice({
      inputNum: data.credit_amount,
      currency: data.currency,
      decimalPlaces: 2,
    }),
    venueData: data.venue,
    isMembershipTicketExchange: data.is_membership_ticket_exchange || false,
    isItemizedExchange: data.is_itemized || false,
    totalTickets: data.total_tickets || null,
  };
}

/**
 * Manages exchange credit state for public-facing (customer) checkout flows.
 *
 * Reads active exchange credit data from localStorage, exposes credit details
 * (amount, venue, exchange type), and provides a `cancelExchange` operation
 * that clears the credit and empties the basket.
 *
 * Use this hook whenever a public page or checkout step needs to react to an
 * active exchange — for example, to hide addons, show the exchange banner,
 * or restrict payment options.
 *
 * For the box office (venue) counterpart, see `useBoxOfficeExchangeManager`.
 */
export function usePublicExchangeManager(): IPublicExchangeManager {
  const { expireBasket, emptyBasket } = usePublicBasket();
  const { useCancelExchange, invalidateAllRepositoryQueries } =
    useUserBasedDiscountService();
  const { mutate: cancelExchangeMutation } = useCancelExchange({
    showToastOnSuccess: false,
    showToastOnError: false,
  });
  const { userProfileData } = useUserSessionAuthState();

  const [isCancellingExchange, setIsCancellingExchange] = useState(false);
  const [exchangeState, setExchangeState] = useState<ExchangeCreditState>(() =>
    buildExchangeState(readExchangeCreditData())
  );

  const clearCredit = () => {
    if (isStorageAvailable("localStorage")) {
      localStorage.removeItem("exchangeCreditData");
    }
    setExchangeState(buildExchangeState(null));
  };

  const cancelExchange = async () => {
    try {
      setIsCancellingExchange(true);

      // Call API to cancel exchange if needed
      cancelExchangeMutation({
        user_email: userProfileData?.email || "",
      });

      // On success: remove localStorage and expire basket
      clearCredit();
      await expireBasket();
      await emptyBasket();
      invalidateAllRepositoryQueries();
    } catch (error) {
      // On error: check if exchange doesn't exist, then clean up anyway
      const errorDetail =
        (error as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "";
      if (errorDetail.includes("has no itemized exchange credits to cancel")) {
        clearCredit();
        invalidateAllRepositoryQueries();
      } else {
        throw error;
      }
    } finally {
      setIsCancellingExchange(false);
    }
  };

  return {
    formattedAmount: exchangeState.formattedAmount,
    isExchangeActive: exchangeState.isExchangeActive,
    venueData: exchangeState.venueData,
    isMembershipTicketExchange: exchangeState.isMembershipTicketExchange,
    isItemizedExchange: exchangeState.isItemizedExchange,
    totalTickets: exchangeState.totalTickets,
    cancelExchange,
    isCancellingExchange,
  };
}
